import {
  actions, MeetingLeader, ParticipantAutocompleteOption, PrivateExternalParticipantCreate, RootState
} from "../../../store";
import {
  updateMeetingLeader,
  removeMeetingLeader, createMeetingParticipants, updateMeetingParticipant,
  deleteMeetingParticipant,
  addMeetingLeader,
} from "../../../store/schedule/actions";
import { fetchOrganizationLeader } from "../../../store/leaders/actions";
import { useDispatch, useSelector } from "react-redux";
import { useWithDispatch } from "../../../store/hooks";
import { ThunkDispatchType } from "../../../store/types";
import { partial } from 'lodash-es';
import { DateTime } from "luxon";
import { selectLeadersWithCalendars, selectMeeting, selectMeetingLeaders } from "../../../store/schedule/selectors";
import EditParticipantsModal from "./EditParticipantsModal";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { CabModal } from "@CabComponents/CabModal";
import { CabButton } from "@CabComponents/CabButton";
import { crm } from "../../../store/cabinetApi/crm";
import { scheduling } from "../../../store/cabinetApi/scheduling";


const _ = partial.placeholder;

export type EditParticipantsModalContainerProps = {
  open: boolean;
  onClose: () => void;
  meetingId: number;
};


const EditParticipantsModalContainer = ({ open, onClose, meetingId }: EditParticipantsModalContainerProps) => {
  const preventConflict = useSelector((root: RootState) => selectMeeting(root, meetingId)?.prevent_conflict);
  const leadersWithCalendars = useSelector((root: RootState) => selectLeadersWithCalendars(root));
  const orgLeaders = useSelector((root: RootState) => root.leaders.orgLeaders);
  const orgLeadersLoaded = useSelector((root: RootState) => root.leaders.orgLeadersLoaded);
  const meetingLeaders = useSelector((root: RootState) => selectMeetingLeaders(root, meetingId));
  const meetingParticipants = useSelector((root: RootState) => selectMeeting(root, meetingId)?.participants || {});
  const meetingIsPoll = useSelector((root: RootState) => selectMeeting(root, meetingId)?.is_poll || false);
  const [participantAutocompleteSearch, setParticipantAutocompleteSearch] = useState('');
  const dispatch = useDispatch<ThunkDispatchType>();
  const [updateContact] = crm.useAppCrmContactsPartialUpdateMutation();
  const trimmedParticipantAutocompleteSearch = participantAutocompleteSearch.trim();
  const { participantAutocompleteOptions } = crm.useAppCrmContactsListQuery({
    limit: 20,
    offset: 0,
    search: trimmedParticipantAutocompleteSearch,
    hiddenDateIsnull: true,
    primaryEmailIsempty: false,
    isResource: false,
  }, {
    skip: !trimmedParticipantAutocompleteSearch,
    selectFromResult: ({ data }) => ({
      participantAutocompleteOptions: data?.data.flatMap(contact => contact.primary_email ? [{
        contactId: contact.id,
        name: contact.name || '',
        email: contact.primary_email,
        label: contact.name ? `${contact.name} (${contact.primary_email})` : contact.primary_email,
      }] : []) || [],
    }),
  });

  const isSavedMeeting = meetingId > -1;
  const participants = useMemo(() => Object.values(meetingParticipants), [meetingParticipants]);

  const handleAddLeader = useWithDispatch(partial(addMeetingLeader, meetingId));
  const handleFetchOrganizationLeaders = useWithDispatch(fetchOrganizationLeader);
  const handleUpdateLeader = useWithDispatch(partial(updateMeetingLeader, _, meetingId, _, isSavedMeeting));
  const handleRemoveLeader = useWithDispatch(partial(removeMeetingLeader, meetingId));

  const [deleteAttendeeId, setDeleteAttendeeId] = useState<number | null>(null);
  const [removeLeaderId, setRemoveLeaderId] = useState<number | null>(null);

  const addParticipants = (data: Omit<PrivateExternalParticipantCreate, 'meeting'>[]) => (
    createMeetingParticipants(data.map(item => ({ ...item, meeting: meetingId, required: true })), isSavedMeeting)
  );
  const handleAddParticipants = useWithDispatch(addParticipants);
  const [sendOrgLeaderInvite] = scheduling.useAppSchedulingMeetingsSendOrgLeaderInviteCreateMutation();

  const handleUpdateParticipant = useWithDispatch(partial(updateMeetingParticipant, _, meetingId, isSavedMeeting));
  const handleRemoveParticipant = useWithDispatch(partial(deleteMeetingParticipant, _, meetingId, isSavedMeeting));

  const closeDeleteModal = () => {
    setDeleteAttendeeId(null);
    setRemoveLeaderId(null);
  };

  const handleRemoveMeetingLeader = (meetingLeaderId: MeetingLeader['id']) => {
    closeDeleteModal();
    handleRemoveLeader(meetingLeaderId);
  };

  const handleDeleteAttendee = async (attendeeId: number) => {
    closeDeleteModal();
    handleRemoveParticipant(attendeeId);
  };

  const handleFetchAutocompleteOptions = useCallback(async (value: string) => {
    setParticipantAutocompleteSearch(value);
  }, []);

  const handleUnhideAutocompleteContact = useCallback(async (contact: ParticipantAutocompleteOption) => {
    if (!contact.contactId) return;

    await updateContact({
      id: contact.contactId,
      patchedContact: { hidden_date: null },
    }).unwrap();

    dispatch(actions.globalMessage.removeMessage());
    dispatch(actions.globalMessage.sendMessage({
      active: true,
      severity: "success",
      timeout: 8000,
      message: `${contact.name || contact.email} has been restored to participant search.`,
      autoDismiss: true,
      header: "Contact restored",
      position: {
        vertical: "bottom",
        horizontal: "left",
      },
    }));
  }, [dispatch, updateContact]);

  const handleHideAutocompleteContact = useCallback(async (contact: ParticipantAutocompleteOption) => {
    if (!contact.contactId) return;

    await updateContact({
      id: contact.contactId,
      patchedContact: { hidden_date: DateTime.now().toISO() },
    }).unwrap();

    dispatch(actions.globalMessage.sendMessage({
      active: true,
      severity: "success",
      timeout: 10000,
      message: `${contact.name || contact.email} has been hidden. `
        + "To unhide this contact, go to the CRM and search for them.",
      autoDismiss: true,
      header: "Contact hidden",
      position: {
        vertical: "bottom",
        horizontal: "left",
      },
      action: (
        <CabButton
          buttonType="text"
          size="small"
          onClick={() => handleUnhideAutocompleteContact(contact)}
          sx={{ minWidth: 0 }}
        >
          Undo
        </CabButton>
      ),
    }));
  }, [dispatch, handleUnhideAutocompleteContact, updateContact]);

  const handleInviteOrgLeader = useCallback(async (leaderId: number) => {
    await sendOrgLeaderInvite({
      id: `${meetingId}`,
      meetingRequest: { leader_id: leaderId } as unknown as Parameters<typeof sendOrgLeaderInvite>[0]["meetingRequest"],
    }).unwrap();
  }, [meetingId, sendOrgLeaderInvite]);

  useEffect(() => {
    if (open && !orgLeadersLoaded) {
      handleFetchOrganizationLeaders();
    }
  }, [handleFetchOrganizationLeaders, open, orgLeadersLoaded]);

  return (
    <>
      <EditParticipantsModal
        open={open}
        onClose={onClose}
        meetingIsSaved={isSavedMeeting}
        meetingIsPoll={meetingIsPoll}
        participants={participants}
        meetingLeaders={meetingLeaders}
        addLeaderOptions={leadersWithCalendars}
        orgLeaders={orgLeaders}
        participantAutoCompleteOptions={participantAutocompleteOptions}
        meetingPreventConflicts={preventConflict}
        onAddLeader={handleAddLeader}
        onUpdateLeader={handleUpdateLeader}
        onRemoveLeader={setRemoveLeaderId}
        onAddParticipants={handleAddParticipants}
        onUpdateParticipant={handleUpdateParticipant}
        onDeleteParticipant={setDeleteAttendeeId}
        onFetchAutocompleteOptions={handleFetchAutocompleteOptions}
        onHideAutocompleteContact={handleHideAutocompleteContact}
        onInviteOrgLeader={handleInviteOrgLeader}
      />
      {(!!deleteAttendeeId || !!removeLeaderId) && (
        <CabModal
          open={!!deleteAttendeeId || !!removeLeaderId}
          title={'Remove Attendee'}
          onClose={closeDeleteModal}
          isAlert={true}
          noFullScreen={true}
          text={meetingIsPoll 
            ? 'All votes for times that this person submitted will be removed.' 
            : 'Are you sure you want to remove this person from the meeting?'}
          actionButtons={
            <>
              <CabButton buttonType='secondary' onClick={closeDeleteModal}>
                Cancel
              </CabButton>
              <CabButton onClick={ deleteAttendeeId ? () => handleDeleteAttendee(deleteAttendeeId)
                : removeLeaderId ? () => handleRemoveMeetingLeader(removeLeaderId) : undefined} 
              color="error"
              >
                Delete
              </CabButton>
            </>
          }
        />
      )}
    </>
  );
};

export default memo(EditParticipantsModalContainer);
